import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { Button } from "@whoosh/design-system/src/components/controls";
import { PopUpModal } from "@whoosh/design-system/src/components/overlay/PopUpModal";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { EventSignUpProcessSteps } from "@whoosh/features/src/Events/features/AddorEditSignUp";
import { mergeEligibleWindowCharges } from "@whoosh/features/src/Events/utils/mergeEligibleWindowCharges";

import { currentProfileAtom } from "../../../../features/Club/atoms";
import { useFlags } from "../../../../utils/launchDarkly";
import { openEventSignUpModalAtom } from "../../atoms/actions/openEventSignUpModal/openEventSignUpModalAtom";
import { useEventDetailLoginRedirect } from "../../hooks/useEventDetailLoginRedirect";
import { EventDetailCancelWindow } from "../EventDetailCancelWindow";
import { EventDetailSignUpCard_event$key } from "./__generated__/EventDetailSignUpCard_event.graphql";
import { formatSignupDeadline } from "./formatSignupDeadline";

type EventDetailSignUpCardProps = {
  eventRef: EventDetailSignUpCard_event$key;
};

export const EventDetailSignUpCard = ({
  eventRef,
}: EventDetailSignUpCardProps) => {
  const { currencySymbol } = useGetClubCurrency();
  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "EventDetail">>();
  const { joinEvent, openSignupStep } = route.params;
  const event = useFragment(EventDetailSignUpCardFragment, eventRef);

  const eventSignupWindows = event.clientEligibleSignUpWindows;
  const accessStatusForClient = event.accessStatusForClient;
  const signUpStatusForClient = event.signUpStatusForClient;

  const [isJoinEventModalOpen, setIsJoinEventModalOpen] = useState(false);

  const currentProfile = useAtomValue(currentProfileAtom);
  const registrantId = currentProfile?.memberId;
  const { newPatronEventBookingFlow } = useFlags();

  const isGroupEvent =
    event?.maxGuestCount !== 0 ||
    event?.maxMemberCount !== 1 ||
    event?.minMemberCount !== 1 ||
    event?.minGuestCount !== 0;

  const openEventSignUpModal = useSetAtom(openEventSignUpModalAtom);

  const handleGroupEventRegistration = useCallback(() => {
    openEventSignUpModal();
  }, [openEventSignUpModal]);

  const handleAddToWaitlist = useCallback(() => {
    if (registrantId) {
      handleGroupEventRegistration();
    }
  }, [handleGroupEventRegistration, registrantId]);

  const isClosed = ["CLOSED", "COMPLETE"].includes(accessStatusForClient || "");

  const closesAtWindows = eventSignupWindows?.filter((suw) => !!suw?.closesAt);
  const opensAtWindows = eventSignupWindows?.filter((suw) => !!suw?.opensAt);
  const cancelAtWindows = eventSignupWindows?.filter(
    (suw) => !!suw?.cancelDeadlineAt
  );

  const closesAt = closesAtWindows?.[0]?.closesAt ?? null;
  const opensAt = opensAtWindows?.[0]?.opensAt ?? null;
  const cancelAt = cancelAtWindows?.[0]?.cancelDeadlineAt ?? null;

  const handleSignIn = useEventDetailLoginRedirect(
    event.eventSeries?.facility?.club?.slug ?? "",
    event.id ?? ""
  );

  const handleJoinPress = useCallback(() => {
    if (currentProfile?.memberId) {
      setIsJoinEventModalOpen(false);
      handleGroupEventRegistration();
    } else {
      handleSignIn(true);
    }
  }, [currentProfile?.memberId, handleGroupEventRegistration, handleSignIn]);

  const showConfirmationModalSignUp =
    !newPatronEventBookingFlow &&
    !isGroupEvent &&
    !event.memberSignUpProxyAllowed &&
    !!currentProfile?.memberId;

  const canJoinEvent =
    signUpStatusForClient === "NONE" &&
    accessStatusForClient === "SIGNUP_AVAILABLE" &&
    !isClosed;

  const canAddToWaitlist =
    signUpStatusForClient === "NONE" &&
    accessStatusForClient === "WAITLIST_AVAILABLE" &&
    !isClosed;

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList, "EventDetail">>();

  useEffect(() => {
    if (
      joinEvent &&
      Platform.OS === "web" &&
      (canJoinEvent || canAddToWaitlist)
    ) {
      if (showConfirmationModalSignUp) {
        setIsJoinEventModalOpen(true);
      } else if (openSignupStep) {
        openEventSignUpModal({
          overrideStep: openSignupStep as EventSignUpProcessSteps,
        });
      } else {
        handleJoinPress();
      }
      navigation.setParams({ joinEvent: undefined, openSignupStep: undefined });
    }
  }, [
    joinEvent,
    openSignupStep,
    canJoinEvent,
    canAddToWaitlist,
    showConfirmationModalSignUp,
    handleJoinPress,
    openEventSignUpModal,
    navigation,
  ]);

  // Pricing data — charges are sourced from the windows the client is
  // eligible for, merged and de-duped across windows.
  const charges = mergeEligibleWindowCharges(
    event.clientEligibleSignUpWindows
  ).filter((charge) => charge.memberControl !== "HIDDEN");

  const hasPricing = charges.length > 0;

  // Determine card border color and button state
  const isPending = accessStatusForClient === "PENDING";
  const isSignupOpen = accessStatusForClient === "SIGNUP_AVAILABLE";
  const isWaitlistAvailable = accessStatusForClient === "WAITLIST_AVAILABLE";
  const isFull = accessStatusForClient === "FULL";
  const isComplete = accessStatusForClient === "COMPLETE";
  const isSignupClosed = accessStatusForClient === "CLOSED";

  const isButtonDisabled = isPending || isFull || isClosed;
  const borderColor = isButtonDisabled ? "grayscale-200" : "main-primary-300";
  const joinButtonText = isSignupClosed ? t`Closed` : t`Join Event`;

  // Build pill text
  const getPillText = () => {
    if (isPending && opensAt) {
      return `${t`Signup opens`} ${formatSignupDeadline(opensAt)}`;
    }
    if ((isSignupOpen || isWaitlistAvailable) && closesAt) {
      return `${t`Signup closes`} ${formatSignupDeadline(closesAt)}`;
    }
    if (isFull) {
      return t`Event is full`;
    }
    if (isSignupClosed) {
      return t`Closed`;
    }
    if (isClosed && isComplete) {
      return t`Event is complete`;
    }
    return null;
  };

  const pillText = getPillText();

  return (
    <Box>
      <Box
        backgroundColor="surface-primary"
        borderWidth={4}
        borderColor={borderColor}
        borderRadius="4"
        p={6}
      >
        {/* Sign Up Header */}
        <Text variant="mainHeading" mb={2}>
          {t`Sign Up`}
        </Text>

        {/* Pricing Section */}
        {hasPricing ? (
          <VStack gap={2} py={2}>
            {/* Pricing subheader */}
            <HStack alignItems="center" justifyContent="space-between" py={2}>
              <Text variant="mainSmallSemiBold" color="text-primary">
                {t`Pricing`}
              </Text>
            </HStack>

            {/* Rate rows */}
            {charges.map((charge) => {
              const displayPrice = _.isNumber(
                charge.node.chargeRuleSet?.chargeRules?.edges?.[0]?.node?.price
              )
                ? (
                    (charge.node.chargeRuleSet?.chargeRules?.edges?.[0]?.node
                      ?.price ?? 0) / 100
                  ).toFixed(2)
                : null;

              return (
                <HStack
                  key={charge.node.id}
                  alignItems="center"
                  justifyContent="space-between"
                  py={2}
                >
                  <VStack flex={1}>
                    <Text variant="mainBaseMedium" color="text-primary">
                      {charge.node.chargeRuleSet?.displayName ?? ""}
                    </Text>
                    {charge.node.chargeRuleSet?.displayDescription ? (
                      <Text variant="mainTiny" color="text-secondary">
                        {charge.node.chargeRuleSet?.displayDescription}
                      </Text>
                    ) : null}
                  </VStack>
                  {displayPrice !== null ? (
                    <Text
                      variant="mainBaseSemiBold"
                      color="text-primary"
                      textAlign="right"
                    >
                      {`${currencySymbol}${displayPrice}`}
                    </Text>
                  ) : null}
                </HStack>
              );
            })}
          </VStack>
        ) : null}

        {isWaitlistAvailable ? (
          <Text variant="mainSmall" color="text-secondary" py={2}>
            {hasPricing
              ? t`You will not be charged automatically; staff will charge you manually if you move off the waitlist.`
              : t`You will be added to the waitlist.`}
          </Text>
        ) : null}

        {/* CTA Button + Pill */}
        <VStack gap={4} alignItems="center" mt={4}>
          {/* Join Event / Join Waitlist Button */}
          {isWaitlistAvailable && !isClosed ? (
            <Button
              width="100%"
              size="large"
              accessibilityLabel={t`Join Waitlist`}
              testID="event-detail-signup-card-join-waitlist-button"
              onPress={handleAddToWaitlist}
            >
              {t`Join Waitlist`}
            </Button>
          ) : (
            <Button
              width="100%"
              size="large"
              isDisabled={isButtonDisabled}
              accessibilityLabel={joinButtonText}
              testID="event-detail-signup-card-join-event-button"
              onPress={
                isButtonDisabled
                  ? undefined
                  : showConfirmationModalSignUp
                  ? () => setIsJoinEventModalOpen(true)
                  : handleJoinPress
              }
            >
              {joinButtonText}
            </Button>
          )}

          {/* Signup Deadline Pill */}
          {pillText ? (
            <Box
              backgroundColor="surface-main-primary-subtle"
              borderRadius="6"
              px={4}
              py={2}
            >
              <Text
                variant="mainSmallSemiBold"
                color="text-primary"
                textAlign="center"
              >
                {pillText}
              </Text>
            </Box>
          ) : null}
        </VStack>
      </Box>

      {/* Join Event Confirmation Modal */}
      <PopUpModal
        isOpen={isJoinEventModalOpen}
        upperTitle={t`Join Event`}
        cancelText={t`Cancel`}
        confirmText={t`Join`}
        onClose={(confirm: boolean) => {
          if (confirm) {
            handleJoinPress();
          } else {
            setIsJoinEventModalOpen(false);
          }
        }}
        content={
          <VStack>
            <Text color="text-secondary">
              {t`Are you sure you'd like to join ${event.eventSeries.name}?`}
            </Text>
          </VStack>
        }
        footerExtraContent={
          cancelAt ? (
            <EventDetailCancelWindow
              wrapperStyle={{
                borderRadius: Platform.OS === "web" ? "8px" : 0,
              }}
              cancelDeadlineAt={cancelAt}
            />
          ) : undefined
        }
      />
    </Box>
  );
};

const EventDetailSignUpCardFragment = graphql`
  fragment EventDetailSignUpCard_event on Event {
    id
    accessStatusForClient(clientId: $memberId)
    signUpStatusForClient(clientId: $memberId)
    primaryChargeType
    maxGuestCount
    maxMemberCount
    minMemberCount
    minGuestCount
    unknownGuestsAllowed
    memberSignUpProxyAllowed
    signUps {
      edges {
        node {
          id
          registrations {
            edges {
              node {
                id
                registrant {
                  id
                }
              }
            }
          }
        }
      }
    }
    eventSeries {
      name
      facility {
        id
        club {
          slug
        }
      }
    }
    clientEligibleSignUpWindows(clientId: $memberId) {
      closeDaysAhead
      closeTime
      closesAt
      openTime
      opensAt
      cancelDeadlineAt
      cancelDeadlineDaysAhead
      cancelDeadlineTime
      charges {
        edges {
          memberControl
          node {
            id
            chargeRuleSet {
              id
              displayName
              displayDescription
              chargeRules {
                edges {
                  node {
                    id
                    price
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
